import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  Rocket,
  ScrollText,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import HussarTopNav from '../components/HussarTopNav';
import AIActSection from '../components/AIActSection';
import PilotSection from '../components/PilotSection';
import ProtocolBadge from '../components/ProtocolBadge';
import useRouteMeta from '../hooks/useRouteMeta';

/**
 * AI Act Polska — dedicated landing page targeting Polish-language
 * intent around AI Act readiness, governance, and human-controlled AI.
 *
 * Safety doctrine (do not change without approval):
 *   • Never promises "guaranteed AI Act compliance".
 *   • Uses "AI Act-ready workflows" / "designed for AI Act-era governance"
 *     / "supports human oversight" wording only.
 *   • Does not offer legal advice or claim to replace compliance staff.
 *   • Always pairs the 30-day pilot CTA with the human-review trust line.
 */
const AIActPolska = () => {
  const { t, i18n } = useTranslation();

  const lang = (i18n.language || 'pl').startsWith('en') ? 'en' : 'pl';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const titlePl = 'AI Act Polska: Operator AI pod kontrolą człowieka | HussarAi';
  const titleEn = 'AI Act Poland: Human-Controlled AI Operator | HussarAi';
  const descPl =
    'HussarAi pomaga organizacjom wdrażać AI w modelu nadzorowanym przez człowieka — z dokumentacją, śladem audytowym i bez automatycznych decyzji.';
  const descEn =
    'HussarAi helps organizations deploy human-controlled AI workflows with documentation, audit trails, and no automated final decisions.';
  const pageUrl = `${origin}/ai-act-polska`;
  const imageUrl = `${origin}/og/ai-act-polska-og.png`;

  useRouteMeta({
    marker: 'ai-act-polska',
    title: lang === 'en' ? titleEn : titlePl,
    description: lang === 'en' ? descEn : descPl,
    canonical: pageUrl,
    locale: lang === 'en' ? 'en_GB' : 'pl_PL',
    ogImage: imageUrl,
    ogImageWidth: 1200,
    ogImageHeight: 630,
    ogUrl: pageUrl,
  });

  const includedItems = t('aiActPage.pilot.includedItems', { returnObjects: true }) || [];
  const notIncludedItems = t('aiActPage.pilot.notIncludedItems', { returnObjects: true }) || [];

  return (
    <div className="hussar-landing" data-testid="aiact-polska-page">
      <HussarTopNav />

      {/* Hero */}
      <section
        className="hussar-section hussar-hero"
        data-testid="aiact-hero"
        style={{ paddingTop: 80 }}
      >
        <div className="hussar-container">
          <div
            className="hussar-hero-eyebrow"
            data-testid="aiact-hero-eyebrow"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px',
              borderRadius: 999,
              border: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(125, 211, 252, 0.06)',
              color: '#7DD3FC',
              fontSize: 11,
              letterSpacing: 1.2,
              fontWeight: 600,
              marginBottom: 18,
            }}
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            {t('aiActPage.heroEyebrow')}
          </div>
          <h1
            className="hussar-hero-title"
            data-testid="aiact-hero-h1"
            style={{ maxWidth: 880 }}
          >
            {t('aiActPage.heroH1')}
          </h1>
          <p
            className="hussar-hero-subtitle"
            data-testid="aiact-hero-subtitle"
            style={{ maxWidth: 760, marginTop: 16 }}
          >
            {t('aiActPage.heroSubtitle')}
          </p>
          <div
            className="hussar-hero-cta-row"
            data-testid="aiact-hero-cta-row"
            style={{ marginTop: 28, display: 'flex', gap: 12, flexWrap: 'wrap' }}
          >
            <Link
              to="/operator-zgodnosci"
              className="hussar-btn-primary hussar-btn-large"
              data-testid="aiact-hero-cta-compliance"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '14px 24px',
                borderRadius: 8,
                textDecoration: 'none',
                fontWeight: 600,
              }}
            >
              {t('aiActPage.heroCtaCompliance')} <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#pilot"
              className="hussar-btn-secondary"
              data-testid="aiact-hero-cta-pilot"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '14px 24px',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.12)',
                textDecoration: 'none',
                fontWeight: 600,
                color: 'var(--hussar-text-primary, #E5E7EB)',
              }}
            >
              {t('aiActPage.heroCtaPilot')} <Rocket className="w-4 h-4" />
            </a>
          </div>
          <p
            data-testid="aiact-hero-trust-line"
            style={{ marginTop: 22, fontSize: 12, opacity: 0.65, fontStyle: 'italic' }}
          >
            {t('aiActPage.trustLine')}
          </p>
          <ProtocolBadge testidPrefix="aiact-hero" />
        </div>
      </section>

      {/* 5 doctrinal cards — same component as the homepage */}
      <AIActSection variant="aiact-page" ctaHref="#pilot" />

      {/* Safety boundaries — important for compliance / procurement */}
      <section
        className="hussar-section"
        data-testid="aiact-safety-section"
        style={{ paddingTop: 0 }}
      >
        <div className="hussar-container">
          <Card
            data-testid="aiact-safety-card"
            style={{
              padding: 24,
              borderLeft: '3px solid #F59E0B',
              background: 'rgba(245, 158, 11, 0.04)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <AlertTriangle className="w-5 h-5" style={{ color: '#F59E0B', flexShrink: 0, marginTop: 2 }} />
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 8px' }}>
                  {t('aiActPage.safety.heading')}
                </h3>
                <p style={{ fontSize: 14, lineHeight: 1.6, opacity: 0.85, margin: 0 }}>
                  {t('aiActPage.safety.body')}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* 30-day pilot offer */}
      <section
        id="pilot"
        className="hussar-section"
        data-testid="aiact-pilot-section"
      >
        <div className="hussar-container">
          <p className="hussar-section-eyebrow" data-testid="aiact-pilot-eyebrow">
            {t('aiActPage.pilot.eyebrow')}
          </p>
          <h2
            className="hussar-section-title"
            data-testid="aiact-pilot-heading"
            style={{ maxWidth: 820 }}
          >
            {t('aiActPage.pilot.heading')}
          </h2>
          <p
            className="hussar-section-subtitle"
            data-testid="aiact-pilot-subheading"
            style={{ maxWidth: 760 }}
          >
            {t('aiActPage.pilot.subheading')}
          </p>

          <div
            data-testid="aiact-pilot-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 16,
              marginTop: 24,
            }}
          >
            <Card
              data-testid="aiact-pilot-included"
              style={{ padding: 22 }}
            >
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 600, margin: '0 0 14px' }}>
                <CheckCircle2 className="w-4 h-4" style={{ color: '#10B981' }} />
                {t('aiActPage.pilot.includedHeading')}
              </h3>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.8, opacity: 0.9 }}>
                {Array.isArray(includedItems) && includedItems.map((item, i) => (
                  <li key={item} data-testid={`aiact-pilot-included-${i}`}>{item}</li>
                ))}
              </ul>
            </Card>

            <Card
              data-testid="aiact-pilot-not-included"
              style={{ padding: 22 }}
            >
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 600, margin: '0 0 14px' }}>
                <ScrollText className="w-4 h-4" style={{ color: '#F59E0B' }} />
                {t('aiActPage.pilot.notIncludedHeading')}
              </h3>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.8, opacity: 0.9 }}>
                {Array.isArray(notIncludedItems) && notIncludedItems.map((item, i) => (
                  <li key={item} data-testid={`aiact-pilot-not-included-${i}`}>{item}</li>
                ))}
              </ul>
            </Card>
          </div>

          <p
            data-testid="aiact-pilot-slots"
            style={{
              marginTop: 22,
              fontSize: 13,
              opacity: 0.8,
              fontWeight: 600,
              letterSpacing: 0.3,
            }}
          >
            {t('aiActPage.pilot.slotsLine')}
          </p>
        </div>
      </section>

      {/* Reuse existing PilotSection form below for actual signup */}
      <PilotSection />

      {/* Final CTA */}
      <section className="hussar-section hussar-cta-section" data-testid="aiact-final-cta-section">
        <div className="hussar-container">
          <div className="hussar-cta-content">
            <h2 className="hussar-cta-title" data-testid="aiact-final-cta-title">
              {t('aiActPage.finalCta.title')}
            </h2>
            <p className="hussar-cta-subtitle" data-testid="aiact-final-cta-subtitle">
              {t('aiActPage.finalCta.subtitle')}
            </p>
            <Button
              asChild
              className="hussar-btn-primary hussar-btn-large"
              data-testid="aiact-final-cta-btn"
            >
              <Link to="/operator-zgodnosci">
                {t('aiActPage.finalCta.button')}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIActPolska;
